import { LoadingCard } from '@/components/Base/LoadingCard/LoadingCard'
import { NoDataToDisplayCard } from '@/components/Base/NoDataToDisplayCard/NoDataToDisplayCard'
import { CreateReportModal } from '@/components/CreateReportModal/CreateReportModal'
import { ReportSchedulesAccordion } from '@/components/ReportSchedulesAccordion/ReportSchedulesAccordion'
import { ReportTemplateAccordion } from '@/components/ReportTemplateAccordion/ReportTemplateAccordion'
import { RunScenarioModal } from '@/components/RunScenarioModal/RunScenarioModal'

import { NO_ACCESS_ERR_MSG } from '@/constants/common.constant'
import { UserPermission } from '@/enums/scenario-builder.enum'
import { useAppDispatch } from '@/hooks/use-app-dispatch'
import { useAppSelector } from '@/hooks/use-app-selector'
import { usePortfolioAndBenchmarkLists } from '@/hooks/usePortfolioAndBenchmarkLists'

import { DeleteScenarioModal } from '@/components/DeleteTemplateModal/DeleteTemplateModal'
import { GFPATemplateDetails } from '@/components/ScenarioBuilder/GFPATemplateDetails/GFPAHTemplateDetails'
import { ScenarioTemplateDetails } from '@/components/ScenarioBuilder/ScenarioTemplateDetails/ScenarioTemplateDetails'
import { Toaster } from '@/components/Shared/Toast'
import { ScenarioBuilderLayout } from '@/Layouts/ScenarioBuilderLayout'
import {
  getLoggedInUserInfo,
  userHasPermission,
} from '@/services/scenarioBuilder.service'
import {
  clearState,
  setIsLoading,
  setUserInfo,
} from '@/states/scenarioBuilder.slice'
import { Box } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'

export const ScenarioBuilderPage: React.FC<{ title: string }> = ({ title }) => {
  const dispatch = useAppDispatch()
  const userInfo = useAppSelector((s) => s.scenarioBuilder.userInfo)
  const isLoading = useAppSelector((s) => s.scenarioBuilder.isLoading)
  const isRunReportTemplateModalOpened = useAppSelector(
    (s) => s.scenarioBuilder.isRunReportTemplateModalOpened,
  )
  const isCreateReportModalOpened = useAppSelector(
    (s) => s.scenarioBuilder.isCreateReportModalOpened,
  )
  const isDeleteReportTemplateModalOpened = useAppSelector(
    (s) => s.scenarioBuilder.isDeleteReportTemplateModalOpened,
  )
  const [canViewReportTemplate, setCanViewReportTemplate] =
    useState<boolean>(false)
  useEffect(() => {
    dispatch(clearState())
    const fetchUserInfo = async () => {
      try {
        dispatch(setIsLoading(true))
        const response = await getLoggedInUserInfo()
        dispatch(setUserInfo(response))
      } catch (error) {
        console.error('Failed to fetch user information:', error)
      } finally {
        dispatch(setIsLoading(false))
      }
    }

    fetchUserInfo()
  }, [])

  useEffect(() => {
    if (userInfo) {
      const isUserHasViewPermission = userHasPermission(
        userInfo,
        UserPermission.VIEW_REPORT_TEMPLATE,
      )
      setCanViewReportTemplate(isUserHasViewPermission)
    }
  }, [userInfo])

  // Loads (or refreshes, if stale) the shared portfolio/benchmark lists once
  // permission is confirmed.
  usePortfolioAndBenchmarkLists(canViewReportTemplate)

  const { selectedReportTemplate: reportTemplate } = useSelector(
    (state: RootState) => state.scenarioBuilder,
  )

  return (
    <ScenarioBuilderLayout>
      <>
        {isLoading && <LoadingCard />}
        {!isLoading && !canViewReportTemplate && (
          <NoDataToDisplayCard message={NO_ACCESS_ERR_MSG} />
        )}
        {!isLoading && canViewReportTemplate && (
          <Box display='grid' alignItems='start' padding={2}>
            <ReportSchedulesAccordion />
            <ReportTemplateAccordion />
            {reportTemplate && Object.keys(reportTemplate).length > 0 && (
              <Box sx={{ mt: 2 }}>
                {reportTemplate.reportType === 'GFPAH' ? (
                  <GFPATemplateDetails reportTemplate={reportTemplate} />
                ) : reportTemplate.reportType === 'SCEN' ? (
                  <ScenarioTemplateDetails reportTemplate={reportTemplate} />
                ) : null}
              </Box>
            )}
          </Box>
        )}
        <Toaster />
        {isRunReportTemplateModalOpened && <RunScenarioModal />}
        {isCreateReportModalOpened && <CreateReportModal />}
        {isDeleteReportTemplateModalOpened && <DeleteScenarioModal />}
      </>
    </ScenarioBuilderLayout>
  )
}
